import React, { useReducer } from 'react';

import { useTranslation } from 'react-i18next';

import useWindowStorage from 'components/hooks/useWindowStorage';

import { useCurrentActor } from 'components/contexts/CurrentActor';
import TrainingStatusFilter from 'components/trainingRegisterReports/components/TrainingStatusFilter';
import {
  buildFormValues,
  validateForm,
} from 'components/trainingRegisterReports/helpers';
import useReportForm, {
  initialEstimatesState,
  initialFiltersState,
  initialTrainingStatusFilterState,
} from 'components/trainingRegisterReports/hooks/useReportForm';
import Report from 'components/trainingRegisterReports/reports/Report';
import { trainingStatusFilterSchema } from 'components/trainingRegisterReports/schemas/trainingStatusFilterSchema';
import validationErrorsReducer, {
  initialValidationErrorsState,
  mapValidationErrorsDispatch,
} from 'components/validationErrors/reducers/validationErrorsReducer';

const initialFormValues = {
  trainingStatusFilter: initialTrainingStatusFilterState,
  estimates: initialEstimatesState,
  filters: initialFiltersState,
};

const additionalValidators = [
  { schema: trainingStatusFilterSchema, key: 'trainingStatusFilter' },
];

export default function CurrentTrainingStatusReport() {
  const currentActor = useCurrentActor();

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'trainingRegisterReports.reports.CurrentTrainingStatusReport',
  });

  const { t: i18nTHelpers } = useTranslation('components', {
    keyPrefix: 'trainingRegisterReports.helpers',
  });

  const { t: i18nTTraining } = useTranslation('common', {
    keyPrefix: 'training',
  });

  const [getLocalStorageItem, setLocalStorageItem, removeLocalStorageItem] =
    useWindowStorage(
      `currentTrainingStatusReport|${currentActor.user.id}|${currentActor.division.id}`,
    );

  const {
    formValues,
    handleEstimatesChange,
    handleFilterAddition,
    handleFilterChange,
    handleFilterDeselection,
    handleFilterRemoval,
    handleFilterSelection,
    handleTrainingStatusFilterChange,
  } = useReportForm(
    'currentTrainingStatus',
    initialFormValues,
    (initialState) =>
      buildFormValues({
        type: 'currentTrainingStatus',
        formValues: initialState,
        additionalValidators,
        getLocalStorageItem,
        removeLocalStorageItem,
      }),
  );

  const [validationErrors, validationErrorsDispatch] = useReducer(
    validationErrorsReducer,
    initialValidationErrorsState,
  );
  const validationErrorsHandlers = mapValidationErrorsDispatch(
    validationErrorsDispatch,
  );

  const handleValidate = () =>
    validateForm({
      formValues,
      additionalValidators,
      safe: true,
      translations: i18nTHelpers('validateForm', { returnObjects: true }),
    });

  return (
    <Report
      formValues={formValues}
      onEstimatesChange={handleEstimatesChange}
      onFilterAddition={handleFilterAddition}
      onFilterChange={handleFilterChange}
      onFilterDeselection={handleFilterDeselection}
      onFilterRemoval={handleFilterRemoval}
      onFilterSelection={handleFilterSelection}
      onValidate={handleValidate}
      onValidationErrorsAssignment={
        validationErrorsHandlers.handleValidationErrorsAssignment
      }
      onValidationErrorsReset={
        validationErrorsHandlers.handleValidationErrorsReset
      }
      onValidationErrorsUnhighlight={
        validationErrorsHandlers.handleValidationErrorsUnhighlight
      }
      setLocalStorageItem={setLocalStorageItem}
      title={i18nT('title')}
      tooltip={i18nT('tooltip')}
      type='currentTrainingStatus'
      validationErrors={validationErrors}
    >
      <label className='tw-mb-3 tw-font-medium'>
        {i18nTTraining('trainingStatus')}
      </label>
      <div className='tw-mb-6'>
        <TrainingStatusFilter
          onChange={handleTrainingStatusFilterChange}
          trainingStatusFilter={formValues.trainingStatusFilter}
        />
      </div>
    </Report>
  );
}
